import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } from "@zxing/library";
import { ScanLine, X, AlertTriangle, Loader2, RefreshCw, Camera, Keyboard, ScanBarcode } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const AAMVA_CODES = ["DAA", "DAB", "DAC", "DAD", "DAQ", "DBB", "DCS", "DCT"];

/**
 * Parses an AAMVA PDF417 driver-license barcode string into useful fields.
 * Handles both newline-separated payloads (camera / most scanners) and
 * single-line payloads from keyboard-wedge scanners that strip control chars.
 */
function parseAAMVA(text) {
  const out = { full_name: "", first_name: "", last_name: "", license_number: "", dob: "", raw_length: (text || "").length };
  if (!text) return out;
  let src = text;
  // Wedge scanners often strip \n — re-insert separators before known field codes.
  if (!/[\n\r]/.test(src) && /ANSI/i.test(src)) {
    for (const code of AAMVA_CODES) src = src.split(code).join(`\n${code}`);
  }
  const fields = {};
  const re = /\b([A-Z]{3})([^\n\r]*)/g;
  let m;
  while ((m = re.exec(src)) !== null) {
    if (!fields[m[1]]) fields[m[1]] = m[2].trim();
  }
  out.first_name = fields.DAC || fields.DCT || "";
  out.last_name = fields.DCS || fields.DAB || "";
  out.full_name = (fields.DAA || `${out.first_name} ${out.last_name}`).replace(/,/g, " ").replace(/\s+/g, " ").trim();
  out.license_number = fields.DAQ || "";
  out.dob = fields.DBB || "";
  return out;
}

const MODE_KEY = "logerd_scan_mode";

export default function LicenseScanner({ open, onClose, onScanned }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const wedgeRef = useRef(null);
  const wedgeTimer = useRef(null);
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || "wedge"); // wedge | camera | manual
  const [status, setStatus] = useState("idle"); // idle | scanning | error
  const [error, setError] = useState("");
  const [wedgeBuf, setWedgeBuf] = useState("");
  const [manual, setManual] = useState({ full_name: "", license_number: "" });
  const [manualHint, setManualHint] = useState("");

  const pickMode = (m) => {
    localStorage.setItem(MODE_KEY, m);
    setMode(m);
    setError("");
    setManualHint("");
  };

  const finish = useCallback((data) => {
    onScanned(data);
  }, [onScanned]);

  // Parse whatever the scan produced. Incomplete result -> prefill manual form.
  const handleRawScan = useCallback((txt, photo = "") => {
    const parsed = parseAAMVA(txt);
    const full_name = parsed.full_name || `${parsed.first_name} ${parsed.last_name}`.trim();
    if (full_name && parsed.license_number) {
      finish({ full_name, license_number: parsed.license_number, photo, raw: txt });
      return;
    }
    // Non-AAMVA code (e.g. a plain QR): treat the payload as the ID number and ask for the name.
    setManual({ full_name, license_number: parsed.license_number || txt.trim().slice(0, 60) });
    setManualHint("We read the code but couldn't find both the name and ID on it — please confirm below.");
    setMode("manual");
  }, [finish]);

  // ---------- Camera mode ----------
  const startCamera = useCallback(async () => {
    setStatus("scanning");
    setError("");
    try {
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417, BarcodeFormat.QR_CODE]);
      const reader = new BrowserMultiFormatReader(hints);
      readerRef.current = reader;
      await reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
        if (result) {
          const txt = result.getText();
          const photo = snapshot(videoRef.current);
          stopCamera();
          handleRawScan(txt, photo);
        }
      });
    } catch (e) {
      setStatus("error");
      setError(e?.message || "Couldn't access camera. Check permissions.");
    }
  }, [handleRawScan]);

  const stopCamera = () => {
    try { readerRef.current?.reset(); } catch { /* noop */ }
    readerRef.current = null;
  };

  useEffect(() => {
    if (!open) { stopCamera(); setWedgeBuf(""); return undefined; }
    if (mode === "camera") { startCamera(); return stopCamera; }
    stopCamera();
    if (mode === "wedge") {
      const t = setTimeout(() => wedgeRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  // ---------- Hardware scanner (keyboard wedge) mode ----------
  // Scanner guns "type" the whole barcode very fast; AAMVA payloads contain Enter
  // keystrokes between fields, so we buffer in a textarea and parse after a short pause.
  const onWedgeChange = (e) => {
    const v = e.target.value;
    setWedgeBuf(v);
    if (wedgeTimer.current) clearTimeout(wedgeTimer.current);
    if (v.trim().length > 3) {
      wedgeTimer.current = setTimeout(() => {
        setWedgeBuf("");
        handleRawScan(v);
      }, 350);
    }
  };

  const snapshot = (video) => {
    try {
      const c = document.createElement("canvas");
      c.width = video.videoWidth || 640;
      c.height = video.videoHeight || 480;
      c.getContext("2d").drawImage(video, 0, 0, c.width, c.height);
      return c.toDataURL("image/jpeg", 0.7);
    } catch { return ""; }
  };

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.full_name && !manual.license_number) return;
    finish({ full_name: manual.full_name.trim(), license_number: manual.license_number.trim(), photo: "", raw: "" });
  };

  const tabs = [
    { v: "wedge", label: "Scanner gun", Icon: ScanBarcode },
    { v: "camera", label: "Camera", Icon: Camera },
    { v: "manual", label: "Type it in", Icon: Keyboard },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="license-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" /> Scan driver's license
          </DialogTitle>
          <DialogDescription>
            Use a handheld barcode/QR scanner, the camera, or type the details manually.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2" data-testid="scan-mode-tabs">
          {tabs.map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => pickMode(t.v)}
              data-testid={`scan-mode-${t.v}`}
              className={`h-11 rounded-lg border text-sm font-medium inline-flex items-center justify-center gap-2 transition ${
                mode === t.v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              <t.Icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {mode === "wedge" && (
          <div className="space-y-3 pt-1" data-testid="scan-wedge-panel">
            <div
              className="rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-6 text-center cursor-text"
              onClick={() => wedgeRef.current?.focus()}
            >
              <ScanBarcode className="h-10 w-10 text-primary mx-auto mb-3" />
              <div className="font-display text-lg font-semibold text-slate-900">Ready — pull the trigger</div>
              <div className="text-sm text-slate-600 mt-1">
                Point your USB or Bluetooth scanner at the <b>barcode on the back of the license</b> (or a QR code) and scan.
              </div>
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> {wedgeBuf ? "Reading…" : "Waiting for scan"}
              </div>
              <textarea
                ref={wedgeRef}
                value={wedgeBuf}
                onChange={onWedgeChange}
                onBlur={() => { if (open && mode === "wedge") setTimeout(() => wedgeRef.current?.focus(), 50); }}
                autoFocus
                data-testid="scan-wedge-input"
                className="absolute opacity-0 pointer-events-none h-0 w-0"
                aria-label="Scanner input"
              />
            </div>
            <div className="text-xs text-slate-500">
              Tip: the scanner works like a keyboard — no setup needed. If nothing happens, tap the box above once, then scan again.
            </div>
          </div>
        )}

        {mode === "camera" && (
          <div className="space-y-3 pt-1" data-testid="scan-camera-panel">
            <div className="aspect-video w-full rounded-lg bg-slate-900 overflow-hidden relative">
              <video ref={videoRef} className="w-full h-full object-cover" muted playsInline data-testid="license-scan-video" />
              <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 border-2 border-emerald-400/80 rounded-md h-12 pointer-events-none" />
            </div>
            {status === "error" && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <div>{error}</div>
              </div>
            )}
            {status === "scanning" && (
              <div className="text-xs text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Looking for barcode…</div>
            )}
            <div className="flex justify-end">
              <button onClick={startCamera} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Restart camera</button>
            </div>
          </div>
        )}

        {mode === "manual" && (
          <form onSubmit={submitManual} className="space-y-3 pt-1" data-testid="license-manual-form">
            {manualHint && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="scan-manual-hint">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>{manualHint}</div>
              </div>
            )}
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Full name</span>
              <input required value={manual.full_name} onChange={(e) => setManual({ ...manual, full_name: e.target.value })} data-testid="license-manual-name" className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" placeholder="As shown on ID" />
            </label>
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">DL / ID</span>
              <input value={manual.license_number} onChange={(e) => setManual({ ...manual, license_number: e.target.value })} data-testid="license-manual-num" className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" placeholder="Optional but recommended" />
            </label>
            <div className="flex justify-end pt-1">
              <button type="submit" data-testid="license-manual-submit" className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:shadow">Continue</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
